<template>
  <div class="container box">
    <div class="header">
      <h2 class="title">Offers for {{ decodedAssetName }}</h2>
      <button class="button is-medium is-outlined" @click="$router.replace('/market')">
        <i class="fas fa-arrow-left icon-margin"></i>
        <span>Back to market</span>
      </button>
    </div>
    <hr/>
    <b-notification
      v-if="!hasAccessToTrade"
      type="is-warning"
      aria-close-label="Close notification"
      role="alert">
      Trading is temporarily halted for your account. For more information please contact us
      via email or discord.
    </b-notification>
    <b-tabs position="is-centered" class="block">
      <b-tab-item label="Offers" icon="money-bill">
        <div v-if="saleItemsAreLoaded" class="offers-wrapper">
          <div class="offers-wrapper" v-if="saleItems.length >= 1">
            <div class="best-offer columns" v-if="bestOffer">
              <h2 class="section-title tag has-background-danger has-text-white">Best offer</h2>
              <sale-item
                class="column"
                :sale-item="bestOffer"
                @purchase="purchaseInit(bestOffer)"
                @cancel="cancelInit(bestOffer)"
              />
            </div>
            <div v-if="otherOffers.length > 0">
              <h2 class="section-title tag has-background-blue has-text-white">Other offers</h2>
              <div class="columns other-offers">
                <sale-item
                  class="column is-one-third"
                  v-for="item in otherOffers"
                  :sale-item="item"
                  @purchase="purchaseInit(item)"
                  @cancel="cancelInit(item)"
                />
              </div>
            </div>
          </div>
          <div v-else>
            <h3 class="title">
              There are no active sales for <b>{{assetName}}!</b>
            </h3>
            <button
              class="button is-primary is-large"
              @click="$router.replace('/market')"
            >
              Get back to market
            </button>
          </div>
        </div>
        <div v-else class="loader-wrap">
          <PulseLoader/>
        </div>
      </b-tab-item>
      <b-tab-item label="Trade history" icon="history">
        <div v-if="saleHistoryIsLoaded">
          <AssetSaleHistory :asset-name="assetName" :sale-history="saleHistory"/>
        </div>
        <div v-else class="loader-wrap">
          <PulseLoader/>
        </div>
      </b-tab-item>
    </b-tabs>
  </div>
</template>

<script>

import PulseLoader from 'vue-spinner/src/PulseLoader';
import SaleItem from '../components/SaleItem.vue';
import AssetSaleHistory from '../components/AssetSaleHistory.vue';

export default {
  name: 'NormalSaleItem',
  components: {
    PulseLoader,
    SaleItem,
    AssetSaleHistory,
  },
  data: () => ({
    saleItems: null,
    saleItemsAreLoaded: false,
    saleHistory: null,
    saleHistoryIsLoaded: false,
  }),
  methods: {
    getSaleItems() {
      this.saleItemsAreLoaded = false;
      this.$store.dispatch('get', {
        url: `/sale?assetName=${this.assetName}`,
        onSuccess: (res) => {
          this.saleItems = res.data;
          this.saleItemsAreLoaded = true;
        },
        onFailure: () => {
          this.errorSnackbar('Error loading asset sale items');
        },
      });
    },
    purchaseInit(saleItem) {
      this.$dialog.confirm({
        title: `Purchasing ${saleItem.assetName}`,
        message: `Are you sure you want to <b>purchase</b> ${saleItem.amount} of ${saleItem.assetName} for <b>${saleItem.price} EVR?</b> This action cannot be undone.`,
        confirmText: 'Confirm purchase',
        type: 'is-success',
        hasIcon: true,
        onConfirm: () => {
          this.$store.dispatch('post', {
            url: '/trade/buy',
            body: { saleId: saleItem.id },
            onSuccess: () => {
              this.successSnackbar('Asset is purchased successfully. Transaction is confirming');
              this.getSaleItems();
              // this.loadMySales();
            },
            onFailure: (err) => {
              if (err.response.data.error === 'sale is not live') {
                this.errorSnackbar('Sale is not live');
              } else if (err.response.data.error === 'sale is not found') {
                this.errorSnackbar('Sale is not found');
              } else if (err.response.data.error === 'insufficient balance') {
                this.errorSnackbar('Can\'t purchase asset due to unsufficient balance')
              } else if (err.response.data.error === 'too much requests') {
                this.errorSnackbar('Too much requests. Try again in a second');
              } else {
                this.errorSnackbar('Unknown error. Try again later');
              }
            },
          });
        },
      });
    },
    cancelInit(saleItem) {
      this.$dialog.confirm({
        title: `Cancelling sale for ${saleItem.assetName}`,
        message: `Are you sure you want to <b>cancel</b> sale for ${saleItem.assetName}?`,
        confirmText: 'Confirm cancel',
        type: 'is-success',
        hasIcon: true,
        onConfirm: () => {
          this.$store.dispatch('post', {
            url: `/sale/cancel?saleId=${saleItem.id}`,
            body: {},
            onSuccess: () => {
              this.successSnackbar('Sale is cancelled successfully');
              this.getSaleItems();
              // this.loadMySales();
            },
            onFailure: (err) => {
              if (err.response.data.error === 'too much requests') {
                this.errorSnackbar('Too much requests. Try again in a second');
              } else {
                this.errorSnackbar('Couldn\'t cancel the sale due to server error. Try again later');
              }
            },
          });
        },
      });
    },
    errorSnackbar(text) {
      this.$snackbar.open({
        message: text,
        type: 'is-warning',
        position: 'is-top',
        actionText: 'Ok',
      });
    },
    successSnackbar(text) {
      this.$snackbar.open({
        message: text,
        type: 'is-success',
        position: 'is-top',
      });
    },
    getSaleHistory() {
      this.saleHistoryIsLoaded = false;
      this.$store.dispatch('get', {
        url: `/trade/asset?assetName=${this.assetName}`,
        onSuccess: (res) => {
          this.saleHistory = res.data;
          this.saleHistoryIsLoaded = true;
        },
        onFailure: () => {
          this.errorSnackbar("Couldn't load sale history. Try again later");
        }
      })
    }
  },
  computed: {
    hasAccessToTrade() {
      return this.$store.getters.userCanTrade;
    },
    assetName() {
      return this.$route.params.name
        .replace(/\//g, '%2F')
        .replace(/#/g, '%23');
    },
    decodedAssetName() {
      return this.$route.params.name
        .replace(/%2F/g, '/')
        .replace(/%23/g, '#');
    },
    bestOffer() {
      return this.saleItems && this.saleItems.length >= 1
        ? this.saleItems[0]
        : null;
    },
    otherOffers() {
      return this.saleItems && this.saleItems.length >= 1
        ? [...this.saleItems].slice(1, this.saleItems.length)
        : [];
    },
  },
  watch: {
    '$route.params.name': function() {
      this.getSaleItems();
    },
  },
  mounted() {
    this.getSaleItems();
    this.getSaleHistory();
  },
};

</script>

<style scoped>
.loader-wrap {
  padding: 60px;
}

.best-offer {
  flex-direction: column;
  align-items: center;
}

.other-offers {
  flex-wrap: wrap;
  margin-top: 10px;
}

.title {
  text-align: left;
  margin-bottom: 0px;
}

.section-title {
  font-size: 1.75rem;
  margin-bottom: 16px;
}

.has-background-blue {
  background-color: hsl(217, 71%, 53%);
}

.red {
  color: hsl(14, 100%, 53%);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.icon-margin {
  margin-right: 8px;
}

.offers-wrapper {
  padding-top: 8px;
}
</style>
